import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { configEditorSchema } from './config-editor.js';

/**
 * A GameProfile says how Hearth presents one game: its identity, which modules appear and a
 * few game-aware shortcuts. It is data, not code: it declares capabilities and paths, and
 * anything with behaviour (speaking RCON, querying Modrinth) lives in code the profile can
 * only refer to by name. A profile can never grant a permission; modules still need the
 * caller's own permissions (docs/DESIGN.md §3).
 */

/** Modules a server page can show. A module only appears if it exists in the web app. */
export const PROFILE_MODULES = [
  'overview',
  'console',
  'players',
  'plugins',
  /** The version manager: switch server software and version (docs/SOFTWARE.md). */
  'software',
  'worlds',
  'files',
  'config',
  'backups',
  'startup',
  'network',
  'schedules',
  'databases',
  'users',
  'activity',
  'settings',
  /** Rust: Oxide/Carbon plugins (.cs files in the framework's plugins folder). */
  'oxide',
  /** Rust: map and full wipes. */
  'wipe',
] as const;
export type ProfileModule = (typeof PROFILE_MODULES)[number];

/** The fixed icon set; the web maps each name to a glyph. */
export const PROFILE_ICONS = [
  'pickaxe',
  'box',
  'flame',
  'swords',
  'sprout',
  'crosshair',
  'bot',
  'boxes',
] as const;

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour such as #5fbf4a.');
export const profileSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, digits and dashes.');

export const gameProfileIdentitySchema = z.object({
  name: z.string().trim().min(1).max(80),
  category: z.enum(['survival', 'sandbox', 'fps', 'simulation', 'other']),
  icon: z.enum(PROFILE_ICONS),
  /** One colour per theme; checked for contrast before it is shipped (see safeAccent). */
  accent: z.object({ dark: hex, light: hex }),
  hero: z.object({ pattern: z.enum(['blocks', 'hex', 'none']).default('none') }).default({
    pattern: 'none',
  }),
  /**
   * Egg variables that hold the game version, in order; the first one the server has is shown
   * as a version chip. Eggs name this variable freely, hence a list.
   */
  versionFrom: z
    .object({ envs: z.array(z.string().min(1).max(191)).min(1).max(8) })
    .nullable()
    .default(null),
});
export type GameProfileIdentity = z.infer<typeof gameProfileIdentitySchema>;

export const worldFoldersSchema = z.object({
  detect: z.literal('level-name'),
  /** The game's own default when the setting is missing. */
  defaultName: z.string().min(1).max(100).default('world'),
  dimensions: z
    .array(z.string().regex(/^\{world\}[A-Za-z0-9_.-]*$/, 'Each must start with {world}.'))
    .min(1)
    .max(8)
    .refine((d) => d[0] === '{world}', 'The first dimension must be exactly {world}.')
    .refine((d) => new Set(d).size === d.length, 'Dimensions must be unique.'),
  marker: z
    .string()
    .regex(/^[A-Za-z0-9_.-]+$/)
    .default('level.dat'),
});
export type WorldFolders = z.infer<typeof worldFoldersSchema>;

const profilePath = z.string().startsWith('/').max(255);

/**
 * What the server importer checks for this game (docs/DESIGN.md §7). Each kind is implemented
 * in the API (services/import-checks.ts); a profile only picks kinds and fills in names. Every
 * game also gets the generic checks (folder not empty, nothing left inside one folder, no
 * archive left unextracted).
 */
export const importCheckSchema = z.discriminatedUnion('kind', [
  /** The file the startup command runs, named by an egg variable; any alternative also counts. */
  z.object({
    kind: z.literal('server-jar'),
    variable: z.string().min(1).max(191),
    alternatives: z.array(z.string().min(1).max(255)).max(8).default([]),
  }),
  /** Minecraft's eula.txt: reported, never accepted on the owner's behalf. */
  z.object({ kind: z.literal('eula') }),
  z.object({
    kind: z.literal('file'),
    path: profilePath,
    label: z.string().min(1).max(80),
    missing: z.enum(['warn', 'fail']).default('warn'),
    hint: z.string().max(300).default(''),
  }),
  /** A world folder named by a key of a .properties file, recognised by a marker file in it. */
  z.object({
    kind: z.literal('world'),
    properties: profilePath,
    key: z.string().min(1).max(100),
    fallback: z.string().min(1).max(100),
    marker: z.string().min(1).max(100),
  }),
  /** The port key of a .properties file; explains whether the egg rewrites it on start. */
  z.object({ kind: z.literal('port'), properties: profilePath, key: z.string().min(1).max(100) }),
  /** CurseForge/Modrinth pack manifests, which the importer doesn't install. */
  z.object({ kind: z.literal('modpack-manifest') }),
]);
export type ImportCheck = z.infer<typeof importCheckSchema>;

/**
 * One matching rule. Every predicate a rule names must hit (AND); a profile matches when any
 * of its rules does. A rule must name at least one predicate.
 */
export const profileMatchRuleSchema = z
  .object({
    /** The egg's features contain this, e.g. "eula" for Minecraft Java. */
    eggFeature: z.string().min(1).max(64),
    /** Regex tested against the egg's name, case-insensitively. */
    eggName: z
      .string()
      .min(1)
      .max(200)
      .refine((p) => {
        try {
          new RegExp(p, 'i');
          return true;
        } catch {
          return false;
        }
      }, 'Not a valid regular expression.'),
    /** The nest's name, case-insensitively. */
    nest: z.string().min(1).max(191),
    eggUuid: z.string().uuid(),
    /** The egg's startup command contains this, case-insensitively. */
    startupContains: z.string().min(1).max(200),
    /** Any of the egg's Docker images contains this, case-insensitively. */
    dockerImageContains: z.string().min(1).max(200),
    /** The egg has a variable with exactly this env name, e.g. "MINECRAFT_VERSION". */
    envVariable: z
      .string()
      .max(191)
      .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'An environment variable name such as SERVER_JARFILE.'),
  })
  .partial()
  .strict()
  .refine((r) => Object.keys(r).length > 0, 'A rule needs at least one predicate.');
export type ProfileMatchRule = z.infer<typeof profileMatchRuleSchema>;

/** Where the installer may search and download from. Each needs its own adapter in code. */
export const INSTALLER_SOURCES = ['modrinth', 'curseforge'] as const;
export type InstallerSource = (typeof INSTALLER_SOURCES)[number];

/**
 * Server software the version manager can install. Each is an adapter in the API
 * (src/software/); a profile only lists which ones an egg may switch between.
 */
export const SOFTWARE_FAMILIES = [
  'paper',
  'purpur',
  'folia',
  'vanilla',
  'fabric',
  'quilt',
  'forge',
  'neoforge',
] as const;
export type SoftwareFamily = (typeof SOFTWARE_FAMILIES)[number];

/**
 * One way to switch software for this game: the `families` an egg may switch between, the egg
 * variable that names the jar the startup command runs (`jarVariable`), and whether the egg's
 * startup also starts from unix_args.txt when it exists (`argsFile`, Forge-style eggs).
 * `when` picks the entry by egg; the first fitting entry wins.
 */
export const softwareEntrySchema = z.object({
  families: z.array(z.enum(SOFTWARE_FAMILIES)).min(1).max(SOFTWARE_FAMILIES.length),
  jarVariable: z
    .string()
    .max(191)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'An environment variable name such as SERVER_JARFILE.'),
  argsFile: z.boolean().default(false),
  when: z.array(profileMatchRuleSchema).max(8).default([]),
});
export type SoftwareEntry = z.infer<typeof softwareEntrySchema>;

const folderPath = z
  .string()
  .regex(/^\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/, 'An absolute folder such as /plugins.')
  .refine((p) => !p.split('/').some((s) => s === '..' || s === '.'), 'No . or .. segments.');

/**
 * One way to install add-ons for this game: from `source`, projects of `kind` built for one
 * of `loaders` (Modrinth's loader names, most specific first), into `folder` (the profile's
 * pluginFolder or modFolder when omitted). `when` picks the entry by egg, with the same rules
 * as profile matching; an empty list fits every egg. The first fitting entry per source wins.
 */
export const installerEntrySchema = z.object({
  source: z.enum(INSTALLER_SOURCES),
  kind: z.enum(['plugin', 'mod']),
  loaders: z
    .array(z.string().regex(/^[a-z0-9_-]{1,32}$/))
    .min(1)
    .max(12),
  folder: folderPath.optional(),
  when: z.array(profileMatchRuleSchema).max(8).default([]),
});
export type InstallerEntry = z.infer<typeof installerEntrySchema>;

export const gameProfileDefinitionSchema = z
  .object({
    schema: z.literal(1),
    slug: profileSlugSchema,
    identity: gameProfileIdentitySchema,
    modules: z.array(z.enum(PROFILE_MODULES)).min(1),
    files: z
      .object({
        /** Chips shown above the file manager's breadcrumbs when the path exists. */
        shortcuts: z
          .array(z.object({ label: z.string().min(1).max(40), path: z.string().startsWith('/') }))
          .max(12)
          .default([]),
        /** Where plugins and mods live; the installer's default folders. */
        pluginFolder: folderPath.nullable().default(null),
        modFolder: folderPath.nullable().default(null),
        /**
         * How the world manager finds worlds. `detect` names the code that reads the active
         * world's name (only `level-name`, from server.properties, today); `dimensions` are the
         * folders that make up one world, `{world}` first; a folder counts as a world when it
         * holds `marker`.
         */
        worldFolders: worldFoldersSchema.nullable().default(null),
      })
      .default({ shortcuts: [], pluginFolder: null, modFolder: null, worldFolders: null }),
    console: z
      .object({
        /** Lines matching `pattern` (a regex) are tinted as `kind`. */
        highlights: z
          .array(
            z.object({
              pattern: z.string().min(1).max(200),
              kind: z.enum(['join', 'leave', 'chat']),
            }),
          )
          .max(20)
          .default([]),
        /** Commands offered by the command box's autocomplete. */
        commands: z.array(z.string().min(1).max(64)).max(200).default([]),
      })
      .default({ highlights: [], commands: [] }),
    /** The plugin/mod installer's sources for this game (docs/DESIGN.md §7). */
    installers: z.array(installerEntrySchema).max(32).default([]),
    /** The version manager's software families for this game (docs/SOFTWARE.md). */
    software: z.array(softwareEntrySchema).max(16).default([]),
    /** Config files the Config tab edits as forms (docs/DESIGN.md §4). */
    config: z
      .array(configEditorSchema)
      .max(20)
      .default([])
      .refine(
        (eds) => new Set(eds.map((e) => e.id)).size === eds.length,
        'Config editor ids must be unique.',
      ),
    /** What the server importer checks for this game (services/import-checks.ts). */
    import: z
      .object({ checks: z.array(importCheckSchema).max(20).default([]) })
      .default({ checks: [] }),
  })
  .superRefine((def, ctx) => {
    def.installers.forEach((entry, i) => {
      const fallback = entry.kind === 'plugin' ? def.files.pluginFolder : def.files.modFolder;
      if (!entry.folder && !fallback)
        ctx.addIssue({
          code: 'custom',
          path: ['installers', i, 'folder'],
          message: `Give a folder, or set files.${entry.kind}Folder.`,
        });
    });
  });
export type GameProfileDefinition = z.infer<typeof gameProfileDefinitionSchema>;

/** A profile as stored and shipped in seeds/profiles/*.json. */
export const gameProfileSeedSchema = z.object({
  definition: gameProfileDefinitionSchema,
  match: z.array(profileMatchRuleSchema),
  /** Higher wins when several profiles match. */
  priority: z.number().int().min(0).max(1000).default(0),
});
export type GameProfileSeed = z.infer<typeof gameProfileSeedSchema>;

/** The slice of an egg that matching can see. */
export interface MatchableEgg {
  uuid: string;
  name: string;
  nestName: string;
  features: readonly string[];
  startup: string | null;
  dockerImages: readonly string[];
  /** Env names of the egg's variables; leave out when unknown (envVariable rules then miss). */
  envVariables?: readonly string[];
}

export function ruleMatches(rule: ProfileMatchRule, egg: MatchableEgg): boolean {
  const lower = (s: string) => s.toLowerCase();
  if (rule.eggFeature !== undefined && !egg.features.includes(rule.eggFeature)) return false;
  if (rule.eggName !== undefined && !new RegExp(rule.eggName, 'i').test(egg.name)) return false;
  if (rule.nest !== undefined && lower(rule.nest) !== lower(egg.nestName)) return false;
  if (rule.eggUuid !== undefined && lower(rule.eggUuid) !== lower(egg.uuid)) return false;
  if (
    rule.startupContains !== undefined &&
    !lower(egg.startup ?? '').includes(lower(rule.startupContains))
  )
    return false;
  if (
    rule.dockerImageContains !== undefined &&
    !egg.dockerImages.some((i) => lower(i).includes(lower(rule.dockerImageContains!)))
  )
    return false;
  if (rule.envVariable !== undefined && !(egg.envVariables ?? []).includes(rule.envVariable))
    return false;
  return true;
}

export interface ResolvableProfile {
  definition: GameProfileDefinition;
  match: readonly ProfileMatchRule[];
  priority: number;
}

/** Generic is the fallback and must always be in the list. */
export const GENERIC_PROFILE_SLUG = 'generic';

/**
 * The highest-priority profile with a matching rule; ties go to the earlier one in the list,
 * so callers sort by something stable. Falls back to the generic profile.
 *
 * `pinned` is the egg's explicit profile (`eggs.profile_slug`): it wins over matching when it
 * is in the list, and is ignored (matching decides) when that profile is disabled or gone.
 */
export function resolveProfile<T extends ResolvableProfile>(
  egg: MatchableEgg,
  profiles: readonly T[],
  pinned?: string | null,
): T {
  if (pinned) {
    const hit = profiles.find((p) => p.definition.slug === pinned);
    if (hit) return hit;
  }
  let best: T | undefined;
  for (const p of profiles) {
    if (p.definition.slug === GENERIC_PROFILE_SLUG) continue;
    if (!p.match.some((r) => ruleMatches(r, egg))) continue;
    if (!best || p.priority > best.priority) best = p;
  }
  const generic = profiles.find((p) => p.definition.slug === GENERIC_PROFILE_SLUG);
  const found = best ?? generic;
  if (!found) throw new Error('No generic game profile is available.');
  return found;
}

/** The installer a server gets: one kind and folder, and the loaders per source. */
export interface ResolvedInstaller {
  kind: 'plugin' | 'mod';
  folder: string;
  sources: { source: InstallerSource; loaders: string[] }[];
}

/**
 * Pick the installer for an egg: the first entry whose `when` fits sets the kind and folder,
 * then each source contributes its first fitting entry of that same kind and folder. Null
 * when the profile has no installer for this egg (vanilla servers, for instance).
 */
export function installerFor(
  def: GameProfileDefinition,
  egg: MatchableEgg,
): ResolvedInstaller | null {
  const folderOf = (e: InstallerEntry) =>
    e.folder ?? (e.kind === 'plugin' ? def.files.pluginFolder : def.files.modFolder);
  const fits = def.installers.filter(
    (e) => folderOf(e) && (!e.when.length || e.when.some((r) => ruleMatches(r, egg))),
  );
  const first = fits[0];
  if (!first) return null;
  const folder = folderOf(first)!;
  const sources: ResolvedInstaller['sources'] = [];
  for (const e of fits) {
    if (e.kind !== first.kind || folderOf(e) !== folder) continue;
    if (sources.some((s) => s.source === e.source)) continue;
    sources.push({ source: e.source, loaders: e.loaders });
  }
  return { kind: first.kind, folder, sources };
}

/** The software entry for an egg: the first whose `when` fits, or null. */
export function softwareFor(def: GameProfileDefinition, egg: MatchableEgg): SoftwareEntry | null {
  return (
    def.software.find((e) => !e.when.length || e.when.some((r) => ruleMatches(r, egg))) ?? null
  );
}

// --- Accent safety -------------------------------------------------------------------

/** The surfaces an accent is drawn on, per theme, and the brand colours to fall back to. */
const SURFACE = { dark: '#171f2a', light: '#ffffff' } as const;
const BRAND = { dark: '#009ddc', light: '#0079ad' } as const;
/** WCAG's 3:1 for non-text UI components. */
const MIN_CONTRAST = 3;

function luminance(hexColour: string): number {
  const channel = (i: number) => {
    const c = parseInt(hexColour.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The profile with any accent that is hard to see on its surface replaced by the brand
 * colour, so a bad accent can never ship an unreadable icon or button.
 */
export function withSafeAccent(def: GameProfileDefinition): GameProfileDefinition {
  const pick = (theme: 'dark' | 'light') =>
    contrastRatio(def.identity.accent[theme], SURFACE[theme]) >= MIN_CONTRAST
      ? def.identity.accent[theme]
      : BRAND[theme];
  return {
    ...def,
    identity: { ...def.identity, accent: { dark: pick('dark'), light: pick('light') } },
  };
}

/** What the admin server list needs: the identity, not the whole definition. */
export const profileSummarySchema = z.object({
  slug: z.string(),
  identity: gameProfileIdentitySchema,
});
export type ProfileSummary = z.infer<typeof profileSummarySchema>;

// --- Admin ---------------------------------------------------------------------------

/** A stored profile as the admin API returns it. */
export const gameProfileSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  /** `builtin` profiles come with Hearth and can only be enabled, disabled or duplicated. */
  source: z.enum(['builtin', 'custom']),
  enabled: z.boolean(),
  priority: z.number().int(),
  definition: gameProfileDefinitionSchema,
  match: z.array(profileMatchRuleSchema),
  /** Eggs that resolve to this profile right now (pinned or matched). */
  eggCount: z.number().int(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});
export type GameProfile = z.infer<typeof gameProfileSchema>;

/** Create a custom profile: the same JSON as a seed file. */
export const gameProfileInputSchema = gameProfileSeedSchema.extend({
  enabled: z.boolean().default(true),
});
export type GameProfileInput = z.input<typeof gameProfileInputSchema>;

/** Change a profile; fields left out stay. Built-in profiles accept `enabled` only. */
export const gameProfileUpdateSchema = z.object({
  definition: gameProfileDefinitionSchema.optional(),
  match: z.array(profileMatchRuleSchema).optional(),
  priority: z.number().int().min(0).max(1000).optional(),
  enabled: z.boolean().optional(),
});
export type GameProfileUpdate = z.input<typeof gameProfileUpdateSchema>;

export const gameProfileDuplicateSchema = z.object({
  slug: profileSlugSchema,
  name: z.string().trim().min(1).max(80).optional(),
});

/** Which eggs a profile's rules would hit, and whether it would win them. */
export const profileMatchResultSchema = z.object({
  data: z.array(
    z.object({
      eggId: z.number().int(),
      eggName: z.string(),
      nestName: z.string(),
      /** The profile the egg uses today. */
      currentSlug: z.string(),
      /** The egg is pinned to this profile, which overrides matching. */
      pinnedSlug: z.string().nullable(),
      /** The profile the egg would use if this one were saved and enabled. */
      winnerSlug: z.string(),
      wins: z.boolean(),
    }),
  ),
  /** Eggs checked in total. */
  eggCount: z.number().int(),
});
export type ProfileMatchResult = z.infer<typeof profileMatchResultSchema>;
